import { Client } from '@hey-api/client-fetch';
import { QueryClient, useQuery, UseQueryResult } from '@tanstack/solid-query';
import { Accessor } from 'solid-js';
import { SplatDto } from './splats-api-service.types';

interface DatasetDto {
  readonly id: string;
}

interface PageResponse<T> {
  readonly items?: readonly T[];
  readonly pageIndex?: number;
  readonly totalItems?: number;
}

const DEFAULT_PAGE_SIZE = 100;

function encodeTenantTrn(tenantTrn: string): string {
  return encodeURIComponent(tenantTrn);
}

export function isViewableSplat(splat: SplatDto): boolean {
  const hasTilesetUrl = splat.tilesetUrl != null && splat.tilesetUrl.length > 0;
  const hasDownloadUrl =
    splat.downloadUrl != null && splat.downloadUrl.length > 0;

  return (
    splat.isDeleted !== true &&
    splat.status === 'downloadable' &&
    (hasTilesetUrl || hasDownloadUrl)
  );
}

async function fetchAllPages<T>(
  client: Client,
  buildUrl: (pageIndex: number, pageSize: number) => string,
  signal?: AbortSignal
): Promise<T[]> {
  const results: T[] = [];
  let pageIndex = 0;
  let totalItems = Number.POSITIVE_INFINITY;

  while (results.length < totalItems) {
    const response = await client.get<PageResponse<T>>({
      url: buildUrl(pageIndex, DEFAULT_PAGE_SIZE),
      signal,
    });

    if (response.error) {
      throw new Error(`Splats API request failed: ${response.error}`);
    }

    const page = response.data;
    if (page == null) {
      break;
    }

    const items = page.items ?? [];
    totalItems = Number(page.totalItems ?? items.length);

    if (items.length === 0) {
      break;
    }

    results.push(...items);
    pageIndex += 1;

    if (items.length < DEFAULT_PAGE_SIZE || results.length >= totalItems) {
      break;
    }
  }

  return results;
}

type ListSplatsQueryOptions = {
  readonly queryKey: readonly [
    'splats',
    string | undefined,
    string | undefined,
  ];
  queryFn(): Promise<SplatDto[]>;
  readonly enabled: boolean;
};

function createListSplatsQueryOptions(
  splatsApi: Accessor<SplatsApiService | undefined>,
  tenantTrn: Accessor<string | undefined>,
  projectId: Accessor<string | undefined>
): ListSplatsQueryOptions {
  return {
    queryKey: ['splats', tenantTrn(), projectId()] as const,
    queryFn: async (): Promise<SplatDto[]> => {
      const api = splatsApi();
      const trn = tenantTrn();
      if (api === undefined || trn === undefined) {
        return [];
      }

      return api.listAllSplats(trn);
    },
    enabled:
      splatsApi() !== undefined &&
      tenantTrn() !== undefined &&
      projectId() !== undefined,
  };
}

/** Registers one splat list observer for the provider lifetime. */
export function useSplatsListQuery(
  splatsApi: Accessor<SplatsApiService | undefined>,
  tenantTrn: Accessor<string | undefined>,
  projectId: Accessor<string | undefined>
): UseQueryResult<SplatDto[], Error> {
  return useQuery(() =>
    createListSplatsQueryOptions(splatsApi, tenantTrn, projectId)
  );
}

export class SplatsApiService {
  constructor(
    private readonly client: Client,
    private readonly queryClient: QueryClient
  ) {}

  public listSplatsQuery(
    tenantTrn: Accessor<string | undefined>,
    projectId: Accessor<string | undefined>
  ): UseQueryResult<SplatDto[], Error> {
    return useQuery(() =>
      createListSplatsQueryOptions(() => this, tenantTrn, projectId)
    );
  }

  public async listAllSplats(
    tenantTrn: string,
    signal?: AbortSignal
  ): Promise<SplatDto[]> {
    const encodedTenantTrn = encodeTenantTrn(tenantTrn);

    const datasets = await fetchAllPages<DatasetDto>(
      this.client,
      (pageIndex, pageSize) =>
        `/tenants/${encodedTenantTrn}/datasets?PageIndex=${pageIndex}&PageSize=${pageSize}`,
      signal
    );

    if (datasets.length === 0) {
      return [];
    }

    const splatsByDataset = await Promise.all(
      datasets.map((dataset) =>
        fetchAllPages<SplatDto>(
          this.client,
          (pageIndex, pageSize) =>
            `/tenants/${encodedTenantTrn}/datasets/${dataset.id}/splats?PageIndex=${pageIndex}&PageSize=${pageSize}`,
          signal
        )
      )
    );

    return splatsByDataset.flat().filter(isViewableSplat);
  }

  public async getSplat(
    tenantTrn: string,
    datasetId: string,
    splatId: string,
    signal?: AbortSignal
  ): Promise<SplatDto> {
    signal?.throwIfAborted();

    const splat = await this.queryClient.fetchQuery({
      queryKey: ['splat', tenantTrn, datasetId, splatId],
      queryFn: async ({ signal: querySignal }): Promise<SplatDto> => {
        const response = await this.client.get<SplatDto>({
          url: `/tenants/${encodeTenantTrn(tenantTrn)}/datasets/${datasetId}/splats/${splatId}`,
          signal: querySignal,
        });

        if (response.error || response.data == null) {
          throw new Error(
            `Failed to load Gaussian splat ${splatId}: ${response.error ?? 'empty response'}`
          );
        }

        return response.data;
      },
      staleTime: 0,
    });

    signal?.throwIfAborted();
    return splat;
  }
}
